import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FieldArray, Formik, setIn, useFormikContext } from 'formik';
import { get } from 'lodash';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import { getAllSearchableContactRoles } from '~/components/communications/ContactUtils';
import ContactTextFieldFormik from '~/components/ContactTextFieldFormik';
import Button from '~/components/core/Atomic/Buttons/Button';
import MenuItem from '~/components/core/Atomic/MenuItem';
import CancelButton from '~/components/core/Buttons/CancelButton';
import CheckboxWithButtonWrapperFormik from '~/components/core/Formik/CheckboxWithButtonWrapperFormik';
import Caption from '~/components/core/TextComponents/Caption';
import Text from '~/components/core/TextComponents/Text';
import useCurrencyFormatter from '~/components/CurrencyFormatterContext';
import { usePaymentsConfiguration } from '~/components/hooks/usePaymentsConfiguration';
import { useVatLabel } from '~/components/hooks/useVatLabel';
import { AddIcon, TrashIcon } from '~/components/icons';
import InlineIconButtonUntyped from '~/components/InlineIconButton';
import useOrganization from '~/components/OrganizationContext';
import type {
  PickedRequestAllocation,
  SettlesRecoveryRequestValue,
} from '~/components/RecoveryRequests/SettlesRecoveryRequestField';
import SettlesRecoveryRequestField, {
  RECOVERED_FROM_CONTACT_FIELD_ID,
  SETTLES_RECOVERY_REQUEST_FIELD_ID,
} from '~/components/RecoveryRequests/SettlesRecoveryRequestField';
import TextFieldFormik, { MonetaryValueTextFieldFormik } from '~/components/TextFieldFormik';
import { reportAxiosError } from '~/Utils';
import cn from '~/Utils/cn';
import { DECIMAL_ZERO, safeDecimal } from '~/Utils/decimalUtils';
import { extractError } from '~/Utils/errors/extractError';
import { CENTS_DECIMAL_PLACES, CENTS_ROUNDING } from '~/Utils/toCents';

import { CapBlockedModal, CapWarningModal } from './CapRecoveryModals';
import VatFormikFields, { getVatIncludedValidationSchema } from './VatFormikFields';

interface SplitGeneralRecoveryDialogProps {
  claimId: number;
  exposureIdOrNullIfGeneralExpenses: number | null;
  onClose: () => void;
  onUpdate: () => void | Promise<void>;
}

type PayableType = 'indemnity' | 'expenses';

interface AllocationRow {
  general_recovery_type: string;
  amount: string | number;
  vat_amount: string | number;
}

interface GeneralRecoveryDialogFormSubmitValuesProps {
  payable_types: PayableType[];
  allocations: Record<PayableType, AllocationRow[]>;
  amount: number;
  recovered_from_contact_id: string | number;
  reference: string;
  check_number: string;
  note: string;
  is_vat_included?: boolean | string;
  settles_recovery_request_id?: SettlesRecoveryRequestValue;
}

interface CapWarningState {
  url: string;
  payload: Record<string, unknown>;
  message: string;
}

// InlineIconButton is a .jsx file; under allowJs its optional props (including `ariaLabel`) are
// inferred as the literal `undefined` type. Cast to the runtime shape so a real label can be passed.
interface InlineIconButtonProps {
  icon: React.ElementType;
  ariaLabel?: string;
  tooltipTitle?: string;
  iconStyle?: React.CSSProperties;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
}
const InlineIconButton = InlineIconButtonUntyped as React.ComponentType<InlineIconButtonProps>;

const NONE_RECOVERY_TYPE_LABEL = 'None';
// None is an explicit pick, so it carries a value of its own; the blank stays free to mean "not picked
// yet" and renders the select empty. Both reach the server as a null recovery type.
const NONE_RECOVERY_TYPE_VALUE = '__none__';
const UNSELECTED_RECOVERY_TYPE = '';

const isRecoveryTypePicked = (value: string | null | undefined): value is string => !!value;

const createEmptyRow = (): AllocationRow => ({
  general_recovery_type: UNSELECTED_RECOVERY_TYPE,
  amount: '',
  vat_amount: '',
});

const DUPLICATE_RECOVERY_TYPE_MESSAGE = 'Each recovery type can be used only once per payable type.';

// None is a slot of its own, so two rows explicitly on None collide like any repeated type. Rows
// still unselected are skipped: they are already blocked by the per-row required rule, and keying
// them together would report a duplicate when the real fault is the missing pick. Wired into the
// dialog schema as a hard backstop behind the disable-used-types UX (the authoritative reject is
// the backend's 400). Takes the row shape partially because Yup infers a looser row type from the
// schema than AllocationRow, and only the recovery type is read here.
const recoveryTypesAreUnique = (rows: Partial<AllocationRow>[] | undefined): boolean => {
  const seen = new Set<string>();
  for (const row of rows ?? []) {
    const key = row.general_recovery_type;
    if (!isRecoveryTypePicked(key)) {
      continue;
    }
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
  }
  return true;
};

const PAYABLE_TYPES: { value: PayableType; label: string }[] = [
  { value: 'indemnity', label: 'Indemnity' },
  { value: 'expenses', label: 'Expenses' },
];

interface RecoveryTypeOption {
  id: string;
  desc?: string;
  active?: boolean;
}

interface GeneralRecoveryTypeConfiguration {
  active?: boolean;
  mandatory?: boolean;
  options?: RecoveryTypeOption[];
}

// A field switched off and an emptied (or fully deactivated) option list are the same thing to the
// dialog: there is no list, so a payable type takes a single untyped allocation.
const buildRecoveryTypeOptions = (
  configuration: GeneralRecoveryTypeConfiguration | undefined
): { value: string; label: string }[] =>
  configuration?.active
    ? (configuration.options ?? [])
        .filter((option) => option.active && option.id)
        .map((option) => ({ value: option.id, label: option.desc || option.id }))
    : [];

// A single recovery-type row within a payable-type group: Recovery Type + Amount + VAT + remove.
// Payable type is carried by the enclosing group, not the row.
const RecoveryLineCard: React.FC<{
  payableType: PayableType;
  index: number;
  isSubmitting: boolean;
  onRemove: () => void;
  hasRecoveryTypes: boolean;
  isRecoveryTypeMandatory: boolean;
  recoveryTypeOptions: { value: string; label: string }[];
  usedRecoveryTypes: Set<string>;
  ownValue: string;
  showVat: boolean;
  vatLabel: string;
  canRemove: boolean;
}> = ({
  payableType,
  index,
  isSubmitting,
  onRemove,
  hasRecoveryTypes,
  isRecoveryTypeMandatory,
  recoveryTypeOptions,
  usedRecoveryTypes,
  ownValue,
  showVat,
  vatLabel,
  canRemove,
}) => {
  const { classes } = useStyles();
  const fieldPrefix = `allocations.${payableType}.${index}`;
  const selectableOptions = isRecoveryTypeMandatory
    ? recoveryTypeOptions
    : [{ value: NONE_RECOVERY_TYPE_VALUE, label: NONE_RECOVERY_TYPE_LABEL }, ...recoveryTypeOptions];

  return (
    <div data-testid={`allocation-line-${payableType}-${index}`} className="flex items-start gap-12">
      {hasRecoveryTypes && (
        <div className="flex-1">
          <TextFieldFormik
            id={`${fieldPrefix}.general_recovery_type`}
            label="Recovery Type"
            select
            fullWidth
            className={classes.textField}
            disabled={isSubmitting}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            // The row starts unselected and a pick is always required here: once a sibling row's use
            // of the last other option disables it, the generic single-option auto-fill would
            // otherwise silently overwrite this row's value.
            autoSelectSingleOption={false}
          >
            {/* @ts-expect-error - TextFieldFormik not yet typed and its type inferrence is flawed */}
            {selectableOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={option.value !== ownValue && usedRecoveryTypes.has(option.value)}
              >
                {option.label}
              </MenuItem>
            ))}
          </TextFieldFormik>
        </div>
      )}
      {/* Amount and VAT share the cell Amount occupies on its own when VAT is not collected */}
      <div className="flex flex-1 gap-12">
        <div className="flex-1">
          <MonetaryValueTextFieldFormik
            id={`${fieldPrefix}.amount`}
            label="Amount"
            className={classes.textField}
            fullWidth
            disabled={isSubmitting}
          />
        </div>
        {showVat && (
          <div className="flex-1">
            <MonetaryValueTextFieldFormik
              id={`${fieldPrefix}.vat_amount`}
              label={vatLabel}
              className={classes.textField}
              fullWidth
              disabled={isSubmitting}
            />
          </div>
        )}
      </div>
      {/* the cell is kept even without the button so the rows above and below stay aligned */}
      <div className="flex items-center pt-16">
        {canRemove && (
          <InlineIconButton
            icon={TrashIcon}
            ariaLabel="Remove recovery type"
            tooltipTitle="Remove recovery type"
            iconStyle={{ display: 'flex' }}
            className={cn(classes.textIcon, classes.hoverableNonFilledIcon)}
            onClick={onRemove}
            disabled={isSubmitting}
          />
        )}
      </div>
    </div>
  );
};

// mirrors the sub-reserves pattern: allocation-row amounts are summed into a read-only total
const TotalAmountUpdater: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<GeneralRecoveryDialogFormSubmitValuesProps>();

  // summed in Decimal and cut to cents at the boundary, in lockstep with the server's
  // Decimal-sum + to_cents — a float reduce drifts (33.3 + 66.6 === 99.89999999999999)
  const total = (['indemnity', 'expenses'] as PayableType[])
    .reduce(
      (sum, type) =>
        (values.allocations[type] ?? []).reduce((acc, row) => acc.plus(safeDecimal(Number(row.amount))), sum),
      DECIMAL_ZERO
    )
    .toDecimalPlaces(CENTS_DECIMAL_PLACES, CENTS_ROUNDING)
    .toNumber();

  useEffect(() => {
    if (total !== values.amount) {
      setFieldValue('amount', total);
    }
  }, [setFieldValue, total, values.amount]);

  return null;
};

// Keeps grouped allocation rows in step with the checkbox cards: a newly-checked payable seeds one
// empty row, an unchecked payable clears its rows so nothing stale is submitted.
const PayableTypeSync: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<GeneralRecoveryDialogFormSubmitValuesProps>();

  useEffect(() => {
    (['indemnity', 'expenses'] as PayableType[]).forEach((type) => {
      const selected = values.payable_types.includes(type);
      const rows = values.allocations[type] ?? [];
      if (selected && rows.length === 0) {
        setFieldValue(`allocations.${type}`, [createEmptyRow()]);
      } else if (!selected && rows.length > 0) {
        setFieldValue(`allocations.${type}`, []);
      }
    });
  }, [values.payable_types, values.allocations, setFieldValue]);

  return null;
};

const SplitGeneralRecoveryDialog: React.FC<SplitGeneralRecoveryDialogProps> = ({
  claimId,
  exposureIdOrNullIfGeneralExpenses,
  onClose,
  onUpdate,
}) => {
  const { classes } = useStyles();
  const [capWarning, setCapWarning] = useState<CapWarningState | null>(null);
  const [capBlockedMessage, setCapBlockedMessage] = useState<string | null>(null);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organizationContactRolesDict } = useOrganization();
  const { includeVatInPaymentScreen, generalRecoveriesConfiguration } = usePaymentsConfiguration();
  const vatLabel = useVatLabel();
  const { currencyFormatter } = useCurrencyFormatter();

  const recoveryTypeConfiguration: GeneralRecoveryTypeConfiguration | undefined =
    generalRecoveriesConfiguration?.general_recovery_type;
  const recoveryTypeOptions = useMemo(
    () => buildRecoveryTypeOptions(recoveryTypeConfiguration),
    [recoveryTypeConfiguration]
  );
  const hasRecoveryTypes = recoveryTypeOptions.length > 0;
  // with no list to pick from there is nothing to mark mandatory
  const isRecoveryTypeMandatory = hasRecoveryTypes && !!recoveryTypeConfiguration?.mandatory;
  // one row per configured type, plus one for the untyped (None) slot when it is offered
  const maxRowsPerPayable = hasRecoveryTypes ? recoveryTypeOptions.length + (isRecoveryTypeMandatory ? 0 : 1) : 1;

  const isGeneralExpenses = exposureIdOrNullIfGeneralExpenses === null;

  // The payable type is the request's own once one is picked, and it is the only one: the settlement
  // link demands every recovery it attributes sit in the allocation the request named, so a second
  // checked type would roll the whole action back on a 400.
  //
  // The row it needs is seeded here rather than left to `PayableTypeSync`, which only runs on the
  // render after this one - the request names one recovery type, and there would be no row to put it
  // in. A payable type the adjuster had already filled in keeps its rows; the recovery type prefills
  // only into a lone row, because a split across several is the adjuster's own division of the money
  // and not something one request answers.
  const applyPickedRequest = React.useCallback(
    (values: Record<string, unknown>, { payableType, generalRecoveryType }: PickedRequestAllocation) => {
      const existingRows = (values.allocations as Record<string, AllocationRow[]> | undefined)?.[payableType] ?? [];
      const rows = existingRows.length > 0 ? existingRows : [createEmptyRow()];
      const nextRows =
        hasRecoveryTypes && generalRecoveryType && rows.length === 1
          ? [{ ...rows[0], general_recovery_type: generalRecoveryType }]
          : rows;

      return setIn(setIn(values, 'payable_types', [payableType]), `allocations.${payableType}`, nextRows);
    },
    [hasRecoveryTypes]
  );

  const initialValues = useMemo<GeneralRecoveryDialogFormSubmitValuesProps>(
    () => ({
      payable_types: isGeneralExpenses ? ['expenses'] : [],
      allocations: { indemnity: [], expenses: isGeneralExpenses ? [createEmptyRow()] : [] },
      amount: 0,
      recovered_from_contact_id: '',
      reference: '',
      check_number: '',
      note: '',
      is_vat_included: '' as unknown as boolean,
      settles_recovery_request_id: '' as SettlesRecoveryRequestValue,
    }),
    [isGeneralExpenses]
  );

  const buildAllocations = (values: GeneralRecoveryDialogFormSubmitValuesProps): Record<string, unknown>[] => {
    const includeVat = !!includeVatInPaymentScreen && !!values.is_vat_included;
    return (['indemnity', 'expenses'] as PayableType[]).flatMap((type) =>
      values.payable_types.includes(type)
        ? (values.allocations[type] ?? [])
            .filter((row) => Number(row.amount) > 0)
            .map((row) => ({
              payable_type: type,
              // None and the no-list-configured blank both mean "no recovery type" to the server
              general_recovery_type:
                row.general_recovery_type === NONE_RECOVERY_TYPE_VALUE ? null : row.general_recovery_type || null,
              amount: Number(row.amount),
              ...(includeVat ? { vat_amount: Number(row.vat_amount) || 0 } : {}),
            }))
        : []
    );
  };

  const buildPayload = (values: GeneralRecoveryDialogFormSubmitValuesProps): Record<string, unknown> => {
    const payload: Record<string, unknown> = {
      allocations: buildAllocations(values),
      recovered_from_contact_id: values.recovered_from_contact_id,
      reference: values.reference,
      check_number: values.check_number,
      note: values.note,
    };

    if (includeVatInPaymentScreen) {
      payload.is_vat_included = values.is_vat_included;
    }

    // Left out entirely when unset, so an adjuster who ignores the field posts exactly the body they
    // post today. Set on the built payload rather than at the call site so the cap-override re-post,
    // which re-sends this very object, carries the link too.
    if (values.settles_recovery_request_id) {
      payload[SETTLES_RECOVERY_REQUEST_FIELD_ID] = values.settles_recovery_request_id;
    }

    return payload;
  };

  const buildUrl = (): string =>
    isGeneralExpenses
      ? `/api/v1/claims/${claimId}/general_expenses/general_recovery_groups`
      : `/api/v1/claims/${claimId}/exposures/${exposureIdOrNullIfGeneralExpenses}/general_recovery_groups`;

  const handleSubmit = async (
    values: GeneralRecoveryDialogFormSubmitValuesProps,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    const url = buildUrl();
    const payload = buildPayload(values);
    try {
      await axios.post(url, payload);
      await onUpdate();
      onClose();
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 409) {
          setCapWarning({ url, payload, message: extractError(error).message });
          setSubmitting(false);
          return;
        }
        if (error.response?.status === 422) {
          setCapBlockedMessage(extractError(error).message);
          setSubmitting(false);
          return;
        }
      }
      await reportAxiosError(error);
      setSubmitting(false);
    }
  };

  const handleCapConfirm = async (reason: string): Promise<void> => {
    if (!capWarning) return;
    try {
      await axios.post(capWarning.url, { ...capWarning.payload, cap_override_reason: reason });
      setCapWarning(null);
      await onUpdate();
      onClose();
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    }
  };

  const buildRowSchema = (requireVat: boolean) =>
    Yup.object().shape({
      amount: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .required('Amount is required')
        .moreThan(0, 'Must be greater than 0'),
      // Whenever there is a list to pick from the row must carry a pick, mandatory or not: when the
      // type is optional None is one of the choices, so a blank means the user has not answered yet.
      ...(hasRecoveryTypes
        ? { general_recovery_type: Yup.string().nullable().required('Recovery type is required') }
        : {}),
      ...(requireVat
        ? {
            vat_amount: Yup.number()
              .transform((value, originalValue) => (originalValue === '' ? null : value))
              .required(`${vatLabel} is required`)
              .min(0, `${vatLabel} cannot be negative`)
              // the ceiling is this allocation's own amount, not the action total, so a VAT that fits
              // the group but not its own line is still rejected; compared in Decimal, mirroring the
              // server-side guard
              .test(
                'vat-not-above-allocation-amount',
                `${vatLabel} cannot be bigger than the allocation amount`,
                (value: number | undefined, context: Yup.TestContext) =>
                  safeDecimal(value).lte(safeDecimal(Number(context.parent.amount)))
              ),
          }
        : {}),
    });

  // Built off the root's is_vat_included so a row's VAT requirement follows the single question;
  // Yup only resolves `when` against siblings, and the question is a sibling of `allocations`.
  const buildAllocationsSchema = (requireVat: boolean) =>
    Yup.object().shape({
      indemnity: Yup.array()
        .of(buildRowSchema(requireVat))
        .test('unique-recovery-type', DUPLICATE_RECOVERY_TYPE_MESSAGE, recoveryTypesAreUnique),
      expenses: Yup.array()
        .of(buildRowSchema(requireVat))
        .test('unique-recovery-type', DUPLICATE_RECOVERY_TYPE_MESSAGE, recoveryTypesAreUnique),
    });

  return (
    <>
      {capBlockedMessage !== null && (
        <CapBlockedModal message={capBlockedMessage} onClose={() => setCapBlockedMessage(null)} />
      )}
      {capWarning && (
        <CapWarningModal
          message={capWarning.message}
          onConfirm={handleCapConfirm}
          onClose={() => setCapWarning(null)}
        />
      )}
      <Formik
        initialValues={initialValues}
        validationSchema={Yup.object().shape({
          allocations: Yup.object().when('is_vat_included', (isVatIncluded: boolean) =>
            buildAllocationsSchema(!!includeVatInPaymentScreen && !!isVatIncluded)
          ),
          amount: Yup.number().moreThan(0, 'Enter at least one allocation amount'),
          recovered_from_contact_id: Yup.number().required('Required'),
          reference: Yup.string(),
          note: Yup.string(),
          ...getVatIncludedValidationSchema(!!includeVatInPaymentScreen),
        })}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {(formikProps) => {
          const { isSubmitting, handleSubmit, values, setFieldValue } = formikProps;
          const showVat = !!includeVatInPaymentScreen && !!values.is_vat_included;
          // A picked request fixes the payable type, so the checkbox cards - and the trash icon on a
          // lone row, which unchecks its type - stop being free while one is named.
          const isPayableTypeLocked = isGeneralExpenses || !!values.settles_recovery_request_id;
          // same Decimal-sum-then-cents discipline as the recovery total above
          const totalVatAmount = (['indemnity', 'expenses'] as PayableType[])
            .reduce(
              (sum, type) =>
                (values.payable_types.includes(type) ? (values.allocations[type] ?? []) : []).reduce(
                  (groupSum, row) => groupSum.plus(safeDecimal(Number(row.vat_amount))),
                  sum
                ),
              DECIMAL_ZERO
            )
            .toDecimalPlaces(CENTS_DECIMAL_PLACES, CENTS_ROUNDING)
            .toNumber();

          return (
            <CardDialog
              open
              isDialog
              fullWidth
              maxWidth="sm"
              onClose={onClose}
              title="Add Recovery Amount"
              preventClose={isSubmitting}
            >
              <TotalAmountUpdater />
              <PayableTypeSync />
              <div className="grid gap-16">
                {/* First, because it determines the fields below it: picking a request fixes the
                    payable type and the payer, and prefills the recovery type. */}
                <SettlesRecoveryRequestField
                  exposureIdOrNullIfGeneralExpenses={exposureIdOrNullIfGeneralExpenses}
                  applyPickedRequest={applyPickedRequest}
                  disabled={isSubmitting}
                />

                <div>
                  <Caption variant={Caption.VARIANTS.LABEL}>Payable Type</Caption>
                  <div className="mt-4 flex gap-12">
                    {PAYABLE_TYPES.filter((option) => option.value === 'expenses' || !isGeneralExpenses).map(
                      (option) => (
                        <div key={option.value} className="flex-1">
                          <CheckboxWithButtonWrapperFormik
                            id="payable_types"
                            value={option.value}
                            text={option.label}
                            // Fill the half-row cell: override the shared card's inline-flex/max-content
                            // sizing so the two cards sit 2-up across the full row width.
                            className="mr-0 flex w-full justify-start"
                            disabled={isSubmitting || isPayableTypeLocked}
                          />
                        </div>
                      )
                    )}
                  </div>
                </div>

                {includeVatInPaymentScreen && <VatFormikFields disabled={isSubmitting} withAmountField={false} />}

                {PAYABLE_TYPES.filter((option) => values.payable_types.includes(option.value)).map((option) => {
                  const groupRows = values.allocations[option.value] ?? [];
                  // only picks occupy a slot; rows still unselected leave every option available
                  const usedRecoveryTypes = new Set(
                    groupRows.map((row) => row.general_recovery_type).filter(isRecoveryTypePicked)
                  );
                  const canAddRow = hasRecoveryTypes && groupRows.length < maxRowsPerPayable;

                  return (
                    <div
                      key={option.value}
                      data-testid={`recovery-group-${option.value}`}
                      className="rounded-lg bg-slate-100 p-16"
                    >
                      <Text weight={Text.WEIGHTS.SEMI_BOLD} className="mb-8">
                        {option.label}
                      </Text>
                      <FieldArray name={`allocations.${option.value}`}>
                        {(arrayHelpers) => (
                          <div className="flex flex-col gap-8">
                            {groupRows.map((row, index) => (
                              <RecoveryLineCard
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                payableType={option.value}
                                index={index}
                                isSubmitting={isSubmitting}
                                hasRecoveryTypes={hasRecoveryTypes}
                                isRecoveryTypeMandatory={isRecoveryTypeMandatory}
                                recoveryTypeOptions={recoveryTypeOptions}
                                usedRecoveryTypes={usedRecoveryTypes}
                                ownValue={row.general_recovery_type}
                                showVat={showVat}
                                vatLabel={vatLabel}
                                // removing the last row unchecks the payable type, so it is only
                                // offered where that checkbox is itself free - otherwise the dialog
                                // would be left with no allocations and no way to get one back
                                canRemove={!(isPayableTypeLocked && groupRows.length <= 1)}
                                onRemove={() => {
                                  if (groupRows.length <= 1) {
                                    setFieldValue(
                                      'payable_types',
                                      values.payable_types.filter((type) => type !== option.value)
                                    );
                                  } else {
                                    arrayHelpers.remove(index);
                                  }
                                }}
                              />
                            ))}
                            {hasRecoveryTypes && (
                              <div>
                                <Button
                                  variant="text"
                                  color="primary"
                                  startIcon={<AddIcon iconColor="currentColor" size={14} />}
                                  onClick={() => arrayHelpers.push(createEmptyRow())}
                                  disabled={isSubmitting || !canAddRow}
                                >
                                  Add Recovery Type
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </FieldArray>
                      {formikProps.submitCount > 0 &&
                        typeof get(formikProps.errors, `allocations.${option.value}`) === 'string' && (
                          <Text variant={Text.VARIANTS.XS} className="text-red-600 mt-4">
                            {DUPLICATE_RECOVERY_TYPE_MESSAGE}
                          </Text>
                        )}
                    </div>
                  );
                })}

                <div className="flex items-center justify-between">
                  <Text weight={Text.WEIGHTS.SEMI_BOLD}>Recovery Total</Text>
                  <Text weight={Text.WEIGHTS.SEMI_BOLD}>{currencyFormatter.format(values.amount)}</Text>
                </div>
                {showVat && (
                  <div className="flex items-center justify-between">
                    <Text weight={Text.WEIGHTS.SEMI_BOLD}>Total {vatLabel}</Text>
                    <Text weight={Text.WEIGHTS.SEMI_BOLD}>{currencyFormatter.format(totalVatAmount)}</Text>
                  </div>
                )}
                {values.amount <= 0 && formikProps.submitCount > 0 && (
                  <Text variant={Text.VARIANTS.XS} className="text-red-600">
                    Enter at least one allocation amount
                  </Text>
                )}

                <div className="w-1/2">
                  {/* Not a free choice while a request is named: `settle()` attributes the money to
                      the request's own payer, so any other contact would record this recovery
                      against a party the request never named. Clearing the request frees it again. */}
                  <ContactTextFieldFormik
                    id={RECOVERED_FROM_CONTACT_FIELD_ID}
                    label="Payee"
                    acceptedRoles={getAllSearchableContactRoles(organizationContactRolesDict)}
                    fixedSearchResults
                    fullWidth
                    disabled={isSubmitting}
                    showOnly={!!values.settles_recovery_request_id}
                  />
                </div>
                <div>
                  <TextFieldFormik
                    id="reference"
                    label="Reference"
                    className={classes.textField}
                    fullWidth
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <TextFieldFormik
                    id="check_number"
                    label="Check Number"
                    className={classes.textField}
                    fullWidth
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <TextFieldFormik
                    id="note"
                    label="Recovery Note"
                    className={classes.textField}
                    fullWidth
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className={classes.buttonsContainer}>
                <CancelButton content="Cancel" disabled={isSubmitting} onClick={onClose} />
                <Button variant="contained" color="primary" disabled={isSubmitting} onClick={() => handleSubmit()}>
                  Add
                </Button>
              </div>
            </CardDialog>
          );
        }}
      </Formik>
    </>
  );
};
export default SplitGeneralRecoveryDialog;
export { NONE_RECOVERY_TYPE_VALUE, recoveryTypesAreUnique };
